import React, { Component } from 'react';
import PropTypes from 'prop-types';
import './StickyContainer.less';

/**
 * This layout component should be used on pages where the footer is "sticky" to the bottom of the page.
 * That is, if the height of the content does not cover the height of the viewport, the StickyFooter will still be
 * sticky (not fixed!) to the bottom of the page. As the StickyFooter is not fixed to the bottom of the page, it
 * remains in the flow of the page and the page is able to be scrolled naturally if there is content after the
 * StickyFooter.
 *
 * Expected usage:
 *
 * <StickyContainer>
 *     <StickyBody />
 *     <StickyFooter />
 * </StickyContainer>
 *
 *
 * An optional modification can be made to the StickyFooter to allow it to become fixed and unfixed to the bottom of
 * the page with certain breakpoints, using the component Waypoint. See usage here:
 *
 * <StickyContainer>
 *     <StickyBody />
 *     <StickyFooter />
 *     <Waypoint /> -- https://github.com/brigade/react-waypoint
 * </StickyContainer>
 * 
 * 
 * NOTE: due to a bug in mobile safari in which `calc(100vh - headerSize - footerSize)` doesn't work properly
 * (because the bookmark bar is counted as "part of the 100vh" so our continue button is hidden), we've added some
 * logic to this file to calculate the "minHeight" of the sticky container.  the goal is to make it fill rest of the
 * page so our sticky footer shows up in the same place during the mobile experience. we are using flexbox and flex-grow
 * to help with this.
 */
export class StickyContainer extends Component {
    static propTypes = {
        children: PropTypes.node.isRequired
    };

    constructor(props) {
        super(props);
        this.containerRef = React.createRef();
        this.state = {
            containerMinHeight: 0
        };
    }

    componentDidMount = () => {
        window.addEventListener('resize', this.handleResizeAndInit);
        this.handleResizeAndInit();
    }

    componentWillUnmount = () => {
        window.removeEventListener('resize', this.handleResizeAndInit);
    }

    handleResizeAndInit = () => {
        /*
            The .StickyContainer is only set to use flexbox in our mobile view.  When we are using flexbox,
            we want to set the minHeight appropriately (based on the window height and the position of the
            .StickyContainer on the page). By doing so, the <StickyFooter /> should align with the bottom
            of the page.
        */
        if (this.containerRef &&
            this.containerRef.current &&
            this.containerRef.current.getBoundingClientRect &&
            window.getComputedStyle &&
            window.getComputedStyle(this.containerRef.current, null).display === 'flex') {
            const containerRect = this.containerRef.current.getBoundingClientRect();
            const pageHeight = window.innerHeight;
            const currentTop = containerRect.top + window.scrollY;
            this.setState({
                containerMinHeight: pageHeight - currentTop
            });
        }
    }

    render() {
        const { containerMinHeight } = this.state;
        const containerStyles = {};
        if (containerMinHeight) {
            containerStyles.minHeight = containerMinHeight;
        }
        return (
            <div
                className="StickyContainer"
                style={containerStyles}
                ref={this.containerRef}
            >
                {this.props.children}
            </div>
        );
    }
}

export default StickyContainer;
